import convert from "convert-units";
import get from "lodash/get";
import set from "lodash/set";
import raf from "raf";
import { META, WEIGHT_UNITS } from "../constants";
import i18n from "../i18n";
import accountHelpers from "../utils/account-helpers";
import convertAll from "../utils/convert-all";
import { getReferrerUrl } from "../utils/referrer-url";
import roundIncrement from "../utils/round-increment";
import {
  getRoleNameById,
  getUserToRequest,
} from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { getLeadRole, hasRole } from "./has-role";

//to know if intercom is already loaded
let isPingRequested = false;

/**
 * Ensure user exists and load it.
 */

function loadUser(ctx) {
  const { req, load, locals, isLoggedIn } = ctx;
  if (!isLoggedIn()) return Promise.resolve();

  const handleActiveUser = async (activeUser) => {
    if (!activeUser) return;

    // sets hasRole() function on activeUser
    activeUser.hasRole = hasRole;

    // sets leadRole permission on active user
    activeUser.leadRole = getLeadRole(activeUser);

    // Set metrics.
    set(locals, "units", activeUser.units || "metric");
    set(locals, "round", Boolean(activeUser.round));
    set(locals, "currentActiveUser", activeUser);

    //if the user is part of an organization, we need to fetch all the organizations permissions and roles to use them to restric the app access
    if (activeUser.organizationId) {
      const [organizationRoles] = await Promise.all([
        load("organizationRoles"),
        load("organizationPermissions"),
        load("organizationOwner"),
      ]);
      const organizationRoleName = activeUser.organizationRoleId
        ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
        : "Coach";
      set(locals, "organizationRoleName", organizationRoleName);
    }

    const organizationOwner = get(locals, "organizationOwner");
    const userToRequest = getUserToRequest(activeUser, organizationOwner);
    set(locals, "activeUserToRequest", userToRequest);
    // sets hasRole() function on userToRequest
    userToRequest.hasRole = hasRole;
    // Parse all custom input data.
    if (req.body) convertAll.call(ctx, req.body, locals.units);
    if (req.query) convertAll.call(ctx, req.query, locals.units);

    const preferredLanguage = activeUser.language || "en";
    if (!process.browser) {
      await i18n.changeLanguage(preferredLanguage);
    } else if (i18n.language !== preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
    }

    if (process.env.NODE_ENV && process.browser) {
      if (global.ga) {
        // Setup user id for google analytics.
        ga("set", "userId", activeUser._id);
      }

      //Only request ping if it's the first time
      if (window.Intercom && !isPingRequested) {
        window.Intercom("boot", {
          app_id: "tyo3o3a8",
          email: activeUser.email,
          user_id: activeUser._id,
          name: activeUser.name,
        });
        isPingRequested = true; //turn this to true, to no request ping every time
      }
    }
    set(locals, "permissions", accountHelpers.getAllPermissions(userToRequest));
  };

  if (req.body.omitActiveUser || req.query.omitActiveUser) {
    return new Promise((resolve) =>
      resolve(handleActiveUser(locals.activeUser))
    );
  }
  return load("activeUser", { user: locals.user._id }).then(handleActiveUser);
}

const ERROR_BODY_ENDPOINTS = ["/flexible-import-template"];

const Init = async (ctx, next) => {
  const { res, req, api, locals, flash, assert } = ctx;

  locals.path = req.path;
  locals.pathname = req.pathname;
  locals.params = req.params;
  locals.query = req.query;
  locals.search = req.search;
  locals.href = req.href;
  locals.origin = req.origin;
  locals.units = "metric";
  locals.modals = [];
  locals.flash = flash;
  locals.assert = assert;
  locals.headers = req.headers;

  locals.title = "MyStrengthBook - ";
  locals.description = META.DESCRIPTION;

  // Utility to convert a value from kg to the preferred units.
  ctx.fromMetric = locals.fromMetric = (
    val,
    units = locals.units,
    round = locals.round
  ) => {
    if (!val) return;
    if (units === "imperial") val = convert(val).from("kg").to("lb");
    return roundIncrement(val, units, round);
  };

  // Utility to add a weight unit on to a unit value.
  ctx.withWeight = locals.withWeight = (
    val = 0,
    units = locals.units,
    round = locals.round
  ) => {
    return `${roundIncrement(val, units, round)} ${WEIGHT_UNITS[units]}`;
  };

  ctx.res.backDontScroll = (alternative = "/dashboard") => {
    res.redirect(
      (getReferrerUrl(req) || alternative).replace(/#.*$/, "") + "#_"
    );
  };

  // used to hide the header when the app is loaded into a web view in the mobile app
  if (process.browser) {
    window.hideHeader =
      window.hideHeader || get(req.query, "hideHeader", false);
  }
  api.on("request", (url, { body, query, headers }) => {
    // Parse all custom input data.
    if (body) convertAll.call(ctx, body, locals.units);
    if (query) convertAll.call(ctx, query, locals.units);
    // Mark user as logged in for api.
    if (ctx.isLoggedIn()) headers.set("auth", locals.user.token);
  });

  api.on("response", (url, { status, statusText, ok, headers }) => {
    // These endpoints answer a 4xx with a body the UI renders itself
    // (`{ error, blocked }`); throwing here would drop it. A 401 still throws
    // so a session refresh-retry keeps working.
    if (
      !ok &&
      status !== 401 &&
      ERROR_BODY_ENDPOINTS.some((path) => String(url).includes(path))
    )
      return;

    // Intercept errors and successes from api calls.
    var err = headers.get("x-error-message");
    ctx.assert(!err, status, err);

    if (!ok) {
      ctx.assert(ok, status, statusText || "An error occurred");
    }

    var suc = headers.get("x-success-message");
    if (suc) ctx.flash("success", suc);
  });

  return loadUser(ctx)
    .then(next)
    .then(() => {
      // client related stuffs
      if (process.browser) {
        // Close mobile nav in browser.
        raf(() => {
          const toggle = document.getElementById("mobile-nav-toggle");
          if (toggle) toggle.checked = false;
        });
      }
    });
};

export default Init;
